/**
 * Swap Interface Component
 * Classic swap UI: USDC/USDT → IDRA
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { BrowserProvider, Contract, JsonRpcProvider } from 'ethers';

// Dedicated RPC provider for read-only calls (independent of MetaMask's RPC)
const rpcProvider = new JsonRpcProvider(
  import.meta.env.VITE_POLYGON_RPC_URL_MAINNET || 'https://polygon.llamarpc.com'
);

interface SwapInterfaceProps {
  onSwap: (token: string, amount: string) => Promise<void>;
  isConnected: boolean;
  provider?: BrowserProvider | null;
  chainId?: number | null;
  /**
   * Режим внутри рамки портала RMS. Меняет ТОЛЬКО плотность: отступы, кегль и
   * склейку подписей в строку. Ни одно поле, ни одна кнопка и ни одна проверка
   * не исчезают — иначе внутри рамки и снаружи это были бы два разных обменника.
   */
  ringkas?: boolean;
}

interface RateData {
  rate: number;
  timestamp: number;
  cached: boolean;
  source: string;
}

const TOKENS = [
  { symbol: 'USDC', name: 'USD Coin', decimals: 6 },
  { symbol: 'USDT', name: 'Tether USD', decimals: 6 }
];

// USDC has two versions on Polygon - we'll auto-detect which one user has
const USDC_ADDRESSES_MAINNET = {
  native: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359',    // Native USDC
  bridged: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'   // Bridged USDC.e
};

const USDC_ADDRESSES_TESTNET = {
  native: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
  bridged: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582'    // Same on testnet
};

const BACKEND_URL = import.meta.env.VITE_SWAP_BACKEND_URL || 'http://localhost:8787';

const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)'
];

const IDRA_TOKEN_ADDRESS_MAINNET = '0x7155f1CfbE45F19b2181DE7C7D9bE01D69CEcCC9';
const IDRA_TOKEN_ADDRESS_TESTNET = '0x7D132A72d7a372dccb1C285039b955c1120C8Df1';

const TOKEN_ADDRESSES_MAINNET = {
  USDC: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
  USDT: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F'
};

const TOKEN_ADDRESSES_TESTNET = {
  USDC: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
  USDT: '0xA02f6adc7926efeBBd59Fd43A84f4E0c0c91e832'
};

export function SwapInterface({ onSwap, isConnected, provider, chainId, ringkas = false }: SwapInterfaceProps) {
  const [selectedToken, setSelectedToken] = useState(TOKENS[0]);
  const [amount, setAmount] = useState('');
  const [idraAmount, setIdraAmount] = useState('');
  const [inputMode, setInputMode] = useState<'payment' | 'idra'>('payment'); // Which field user is editing
  const [rateData, setRateData] = useState<RateData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [idraBalance, setIdraBalance] = useState<string | null>(null);
  const [userIdraBalance, setUserIdraBalance] = useState<string | null>(null);
  const [userTokenBalance, setUserTokenBalance] = useState<string | null>(null);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [selectedUsdcAddress, setSelectedUsdcAddress] = useState<string | null>(null);

  // Fetch rate on mount and every 10 seconds
  useEffect(() => {
    fetchRate();
    const interval = setInterval(fetchRate, 10 * 1000); // 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch IDRA balance from IdraSwap contract
  useEffect(() => {
    if (!provider || !chainId) return;

    const fetchIdraBalance = async () => {
      try {
        const isMainnet = chainId === 137;
        const idraTokenAddress = isMainnet ? IDRA_TOKEN_ADDRESS_MAINNET : IDRA_TOKEN_ADDRESS_TESTNET;
        const idraSwapAddress = isMainnet
          ? import.meta.env.VITE_IDRA_SWAP_ADDRESS_MAINNET
          : import.meta.env.VITE_IDRA_SWAP_ADDRESS_TESTNET;

        console.log('Fetching IDRA balance from contract:', idraSwapAddress);

        const idraToken = new Contract(idraTokenAddress, ERC20_ABI, rpcProvider);
        const balance = await idraToken.balanceOf(idraSwapAddress);
        const decimals = await idraToken.decimals();

        console.log('IDRA balance raw:', balance.toString(), 'decimals:', decimals);

        // Format with Indonesian number format (dot as thousand separator, comma as decimal)
        const balanceFormatted = (Number(balance) / Math.pow(10, Number(decimals))).toLocaleString('id-ID', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        });

        console.log('IDRA balance formatted:', balanceFormatted);

        setIdraBalance(balanceFormatted);
      } catch (error) {
        console.error('Failed to fetch IDRA balance:', error);
        setIdraBalance('0,00'); // Set fallback
      }
    };

    fetchIdraBalance();
    const interval = setInterval(fetchIdraBalance, 30 * 1000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [provider, chainId]);

  // Fetch user's token balance (with auto-detection for USDC) and IDRA balance
  useEffect(() => {
    if (!provider || !chainId || !isConnected) {
      setUserTokenBalance(null);
      setUserIdraBalance(null);
      setWalletAddress(null);
      setSelectedUsdcAddress(null);
      return;
    }

    const fetchUserBalance = async () => {
      try {
        const signer = await provider.getSigner();
        const address = await signer.getAddress();
        setWalletAddress(address);

        const isMainnet = chainId === 137;

        // Fetch user's IDRA balance via RPC provider (not MetaMask)
        const idraTokenAddress = isMainnet ? IDRA_TOKEN_ADDRESS_MAINNET : IDRA_TOKEN_ADDRESS_TESTNET;
        const idraToken = new Contract(idraTokenAddress, ERC20_ABI, rpcProvider);
        const idraUserBalance = await idraToken.balanceOf(address);
        const idraDecimals = await idraToken.decimals();

        const idraBalanceFormatted = (Number(idraUserBalance) / Math.pow(10, Number(idraDecimals))).toLocaleString('id-ID', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2
        });

        setUserIdraBalance(idraBalanceFormatted);

        // Special handling for USDC - check both versions and use the one with higher balance
        if (selectedToken.symbol === 'USDC') {
          const usdcAddresses = isMainnet ? USDC_ADDRESSES_MAINNET : USDC_ADDRESSES_TESTNET;

          console.log('Checking both USDC versions...');

          // Check native USDC balance
          const nativeToken = new Contract(usdcAddresses.native, ERC20_ABI, rpcProvider);
          const nativeBalance = await nativeToken.balanceOf(address);

          // Check bridged USDC.e balance
          const bridgedToken = new Contract(usdcAddresses.bridged, ERC20_ABI, rpcProvider);
          const bridgedBalance = await bridgedToken.balanceOf(address);

          console.log('Native USDC balance:', nativeBalance.toString());
          console.log('Bridged USDC.e balance:', bridgedBalance.toString());

          // Use the version with higher balance
          const useNative = nativeBalance >= bridgedBalance;
          const selectedBalance = useNative ? nativeBalance : bridgedBalance;
          const selectedAddress = useNative ? usdcAddresses.native : usdcAddresses.bridged;

          console.log('Selected USDC version:', useNative ? 'Native' : 'Bridged', selectedAddress);
          setSelectedUsdcAddress(selectedAddress);

          // Format with Indonesian number format
          const balanceFormatted = (Number(selectedBalance) / Math.pow(10, selectedToken.decimals)).toLocaleString('id-ID', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
          });

          console.log('USDC balance formatted:', balanceFormatted);
          setUserTokenBalance(balanceFormatted);
        } else {
          // USDT - single address
          const tokenAddresses = isMainnet ? TOKEN_ADDRESSES_MAINNET : TOKEN_ADDRESSES_TESTNET;
          const tokenAddress = tokenAddresses[selectedToken.symbol as keyof typeof tokenAddresses];

          console.log('Fetching balance for:', selectedToken.symbol, tokenAddress);

          const token = new Contract(tokenAddress, ERC20_ABI, rpcProvider);
          const balance = await token.balanceOf(address);

          console.log('Balance raw:', balance.toString(), 'decimals:', selectedToken.decimals);

          // Format with Indonesian number format
          const balanceFormatted = (Number(balance) / Math.pow(10, selectedToken.decimals)).toLocaleString('id-ID', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
          });

          console.log('Balance formatted:', balanceFormatted);
          setUserTokenBalance(balanceFormatted);
        }
      } catch (error) {
        console.error('Failed to fetch user balance for', selectedToken.symbol, ':', error);
        setUserTokenBalance('0,00');
        setUserIdraBalance('0,00');
      }
    };

    fetchUserBalance();
    const interval = setInterval(fetchUserBalance, 30 * 1000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [provider, chainId, isConnected, selectedToken]);

  const fetchRate = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/swap/rate`);
      const data = await response.json();
      setRateData(data);
    } catch (error) {
      console.error('Failed to fetch rate:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate IDRA amount from payment amount
  const calculateIdraFromPayment = (paymentAmount: string) => {
    if (!paymentAmount || !rateData) return '0.00';
    const idra = parseFloat(paymentAmount) * rateData.rate;
    return idra.toFixed(2);
  };

  // Calculate payment amount from IDRA amount
  const calculatePaymentFromIdra = (idra: string) => {
    if (!idra || !rateData) return '0.00';
    const payment = parseFloat(idra) / rateData.rate;
    return payment.toFixed(2);
  };

  // Handle payment amount input
  const handlePaymentAmountChange = (value: string) => {
    setAmount(value);
    setInputMode('payment');
    if (value && rateData) {
      setIdraAmount(calculateIdraFromPayment(value));
    } else {
      setIdraAmount('');
    }
  };

  // Handle IDRA amount input
  const handleIdraAmountChange = (value: string) => {
    setIdraAmount(value);
    setInputMode('idra');
    if (value && rateData) {
      setAmount(calculatePaymentFromIdra(value));
    } else {
      setAmount('');
    }
  };

  // Update calculations when rate changes
  useEffect(() => {
    if (rateData) {
      if (inputMode === 'payment' && amount) {
        setIdraAmount(calculateIdraFromPayment(amount));
      } else if (inputMode === 'idra' && idraAmount) {
        setAmount(calculatePaymentFromIdra(idraAmount));
      }
    }
  }, [rateData]);

  const handleSwap = async () => {
    if (!amount || !isConnected) return;

    setIsSwapping(true);
    try {
      // For USDC, pass the auto-detected address; for USDT, pass the symbol
      const tokenIdentifier = selectedToken.symbol === 'USDC' && selectedUsdcAddress
        ? selectedUsdcAddress
        : selectedToken.symbol;

      await onSwap(tokenIdentifier, amount);
      setAmount('');
      setIdraAmount('');
    } catch (error) {
      console.error('Swap failed:', error);
    } finally {
      setIsSwapping(false);
    }
  };

  const formatTimestamp = (ts: number) => {
    const date = new Date(ts);
    return date.toLocaleTimeString();
  };

  return (
    <div className={`max-w-md mx-auto bg-white rounded-2xl shadow-xl ${ringkas ? 'p-4' : 'p-6'}`}>
      {/* В компактном режиме заголовок и значок сети идут ОДНОЙ строкой, а не
          друг под другом: это самая дешёвая экономия вертикали без потери смысла. */}
      <div
        className={
          ringkas
            ? 'flex items-center justify-between gap-2 mb-3'
            : 'text-center mb-6'
        }
      >
        <h2 className={ringkas ? 'text-lg font-bold' : 'text-2xl font-bold'}>Swap to IDRA</h2>
        <div
          className={`inline-flex items-center gap-2 bg-purple-100 text-purple-700 rounded-full font-medium ${
            ringkas ? 'px-2 py-0.5 text-xs' : 'mt-2 px-3 py-1 text-sm'
          }`}
        >
          <svg className={ringkas ? 'w-3 h-3' : 'w-4 h-4'} viewBox="0 0 38 33" fill="currentColor">
            <path d="M29 10.2c-.7-.4-1.6-.4-2.4 0L21 13.5l-3.8 2.1-5.5 3.3c-.7.4-1.6.4-2.4 0L5 16.3c-.7-.4-1.2-1.2-1.2-2.1v-5c0-.8.4-1.6 1.2-2.1l4.3-2.5c.7-.4 1.6-.4 2.4 0L16 7.2c.7.4 1.2 1.2 1.2 2.1v3.3l3.8-2.2V7c0-.8-.4-1.6-1.2-2.1l-8-4.7c-.7-.4-1.6-.4-2.4 0L1.2 5C.4 5.4 0 6.2 0 7v9.4c0 .8.4 1.6 1.2 2.1l8.1 4.7c.7.4 1.6.4 2.4 0l5.5-3.2 3.8-2.2 5.5-3.2c.7-.4 1.6-.4 2.4 0l4.3 2.5c.7.4 1.2 1.2 1.2 2.1v5c0 .8-.4 1.6-1.2 2.1L29 28.8c-.7.4-1.6.4-2.4 0l-4.3-2.5c-.7-.4-1.2-1.2-1.2-2.1V21l-3.8 2.2v3.3c0 .8.4 1.6 1.2 2.1l8.1 4.7c.7.4 1.6.4 2.4 0l8.1-4.7c.7-.4 1.2-1.2 1.2-2.1V17c0-.8-.4-1.6-1.2-2.1L29 10.2z"/>
          </svg>
          Polygon Blockchain
        </div>
      </div>

      {/* Rate Display */}
      {rateData && (
        <div
          className={`bg-blue-50 rounded-lg ${
            ringkas ? 'mb-3 px-3 py-2 flex items-baseline justify-between gap-2' : 'mb-6 p-4'
          }`}
        >
          <div className={ringkas ? 'text-xs text-gray-600' : 'text-sm text-gray-600 mb-1'}>
            Exchange Rate
          </div>
          <div className={ringkas ? 'text-sm font-bold' : 'text-xl font-bold'}>
            1 USD = {rateData.rate.toLocaleString()} IDRA
          </div>
        </div>
      )}

      {/* From Token */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Pay</label>
        <div className="flex gap-3">
          <select
            value={selectedToken.symbol}
            onChange={(e) => {
              const token = TOKENS.find(t => t.symbol === (e.target as HTMLSelectElement).value);
              if (token) setSelectedToken(token);
            }}
            className="w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {TOKENS.map(token => (
              <option key={token.symbol} value={token.symbol}>
                {token.symbol}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={amount}
            onChange={(e) => handlePaymentAmountChange((e.target as HTMLInputElement).value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            className="w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
        {/* User Balance Display */}
        {userTokenBalance && isConnected && (
          <div className="mt-2 text-sm text-gray-600">
            Balance: {userTokenBalance} {selectedToken.symbol}
          </div>
        )}
      </div>

      {/* Swap Arrow */}
      <div className="flex justify-center my-2">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      {/* To Token (IDRA) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Receive</label>
        <div className="flex gap-3">
          <div className="w-1/2 px-4 py-3 bg-gray-100 rounded-lg text-gray-700 font-medium">
            IDRA
          </div>
          <input
            type="number"
            value={idraAmount}
            onChange={(e) => handleIdraAmountChange((e.target as HTMLInputElement).value)}
            placeholder="0.00"
            step="0.01"
            min="0"
            className="w-1/2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* User IDRA Balance */}
      <div className="mt-2 text-sm text-gray-600 space-y-1">
        {userIdraBalance && isConnected && (
          <div>Your current IDRA balance: {userIdraBalance} IDRA</div>
        )}
      </div>

      {/* Swap Button */}
      <button
        onClick={handleSwap}
        disabled={!isConnected || !amount || isSwapping || !rateData}
        className="w-full py-4 bg-primary text-white rounded-lg font-semibold hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {!isConnected
          ? 'Connect Wallet to Swap'
          : isSwapping
          ? 'Swapping...'
          : 'Swap'}
      </button>
    </div>
  );
}
